// Permission Utility Functions
// Phase 6 - Part 2: Frontend RBAC Support

/**
 * Get user permissions from localStorage (stored after login)
 * Checks both 'userData' (from JWT) and 'userpermission' (from employee permissions API)
 * @returns {Array<string>} Array of permission strings
 */
export const getUserPermissions = () => {
  try {
    // First check userData (from JWT token)
    const userDataStr = localStorage.getItem('userData');
    if (userDataStr) {
      const userData = JSON.parse(userDataStr);
      if (userData.permissions?.length > 0) {
        return userData.permissions;
      }
    }
    
    // Fallback to userpermission (from employee permissions API)
    const userPermissionStr = localStorage.getItem('userpermission');
    if (userPermissionStr) {
      const userPermission = JSON.parse(userPermissionStr);
      // Convert permission object to array format for checking
      // The userpermission object has structure like: { pos: { aiFeatures: { voiceOrdering: true } } }
      return extractPermissionsFromObject(userPermission);
    }
    
    return [];
  } catch (error) {
    console.error('Error getting user permissions:', error);
    return [];
  }
};

/**
 * Extract permission strings from nested permission object
 * Converts { pos: { aiFeatures: { voiceOrdering: true } } } to ['POS.AI.VOICE_ORDERING']
 */
const extractPermissionsFromObject = (permObj, prefix = '') => {
  const permissions = [];
  
  if (!permObj || typeof permObj !== 'object') return permissions;
  
  Object.entries(permObj).forEach(([key, value]) => {
    const currentPath = prefix ? `${prefix}.${key}` : key;
    
    if (typeof value === 'boolean' && value === true) {
      // Convert camelCase key to UPPER_SNAKE_CASE permission
      const permKey = currentPath.toUpperCase().replace(/([a-z])([A-Z])/g, '$1_$2');
      permissions.push(permKey);
    } else if (typeof value === 'object' && value !== null) {
      // Recurse into nested objects
      permissions.push(...extractPermissionsFromObject(value, currentPath));
    }
  });
  
  return permissions;
};

/**
 * Get user roles from localStorage
 * @returns {Array<string>} Array of role strings
 */
export const getUserRoles = () => {
  try {
    const userDataStr = localStorage.getItem('userData');
    if (!userDataStr) return [];
    
    const userData = JSON.parse(userDataStr);
    return userData.roles || [];
  } catch (error) {
    console.error('Error getting user roles:', error);
    return [];
  }
};

/**
 * Check if user has a specific permission
 * @param {string} permission - Permission to check (e.g., 'POS.AI.VOICE_ORDERING')
 * @returns {boolean} True if user has the permission
 */
export const hasPermission = (permission) => {
  // First try the array-based permissions
  const permissions = getUserPermissions();
  
  if (permissions && permissions.length > 0) {
    // Check for exact match
    if (permissions.includes(permission)) {
      return true;
    }
    
    // Check for wildcard permissions (e.g., HRM.* grants all HRM permissions)
    const parts = permission.split('.');
    for (let i = parts.length - 1; i > 0; i--) {
      const wildcard = parts.slice(0, i).join('.') + '.*';
      if (permissions.includes(wildcard)) {
        return true;
      }
    }
  }
  
  // Also check the nested object format directly (userpermission in localStorage)
  // This handles permissions like POS.AI.VOICE_ORDERING -> pos.aiFeatures.voiceOrdering
  try {
    const userPermissionStr = localStorage.getItem('userpermission');
    if (userPermissionStr) {
      const userPermission = JSON.parse(userPermissionStr);
      const result = checkNestedPermission(userPermission, permission);
      if (result) return true;
    }
  } catch (e) {
    // Ignore parse errors
  }
  
  return false;
};

/**
 * Check if an AI feature is explicitly disabled in permissions
 * Returns true only if the feature is explicitly set to false
 * @param {string} featureKey - Feature key like 'voiceOrdering', 'smartSuggestions', 'customerIntelligence'
 * @returns {boolean} True if explicitly disabled, false otherwise
 */
export const checkAiFeatureDisabled = (featureKey) => {
  try {
    const userPermissionStr = localStorage.getItem('userpermission');
    if (!userPermissionStr) {
      console.log(`🔐 checkAiFeatureDisabled(${featureKey}): No userpermission in localStorage`);
      return false; // No permissions set = not disabled
    }
    
    const userPermission = JSON.parse(userPermissionStr);
    console.log(`🔐 checkAiFeatureDisabled(${featureKey}): Full pointOfSales =`, userPermission?.pointOfSales);
    
    // Check if pointOfSales.aiFeatures exists and the specific feature is explicitly false
    // The structure is: { pointOfSales: { aiFeatures: { voiceOrdering: true/false } } }
    const aiFeatures = userPermission?.pointOfSales?.aiFeatures;
    console.log(`🔐 checkAiFeatureDisabled(${featureKey}): aiFeatures =`, aiFeatures);
    
    if (!aiFeatures) {
      console.log(`🔐 checkAiFeatureDisabled(${featureKey}): No aiFeatures section = not disabled`);
      return false; // AI features section not set = not disabled
    }
    
    // Only return true if explicitly set to false
    if (featureKey in aiFeatures && aiFeatures[featureKey] === false) {
      console.log(`🔐 checkAiFeatureDisabled(${featureKey}): Explicitly DISABLED`);
      return true;
    }
    
    console.log(`🔐 checkAiFeatureDisabled(${featureKey}): Not disabled (value: ${aiFeatures[featureKey]})`);
    return false;
  } catch (e) {
    console.error(`🔐 checkAiFeatureDisabled(${featureKey}): Error`, e);
    return false;
  }
};

/**
 * Check permission in nested object format
 * Maps POS.AI.VOICE_ORDERING to pointOfSales.aiFeatures.voiceOrdering
 */
const checkNestedPermission = (permObj, permission) => {
  if (!permObj || typeof permObj !== 'object') return false;
  
  // Map permission string to object path
  // POS.AI.VOICE_ORDERING -> pointOfSales.aiFeatures.voiceOrdering
  // POS.AI.SMART_SUGGESTIONS -> pointOfSales.aiFeatures.smartSuggestions
  // POS.AI.CUSTOMER_INTELLIGENCE -> pointOfSales.aiFeatures.customerIntelligence
  const permissionMappings = {
    'POS.AI.VOICE_ORDERING': ['pointOfSales', 'aiFeatures', 'voiceOrdering'],
    'POS.AI.SMART_SUGGESTIONS': ['pointOfSales', 'aiFeatures', 'smartSuggestions'],
    'POS.AI.CUSTOMER_INTELLIGENCE': ['pointOfSales', 'aiFeatures', 'customerIntelligence'],
  };
  
  const path = permissionMappings[permission];
  if (!path) return false;
  
  // Navigate the object path
  let current = permObj;
  for (const key of path) {
    if (current && typeof current === 'object' && key in current) {
      current = current[key];
    } else {
      return false;
    }
  }
  
  return current === true;
};

/**
 * Check if user has ANY of the specified permissions (OR logic)
 * @param {Array<string>} permissions - Array of permissions to check
 * @returns {boolean} True if user has at least one permission
 */
export const hasAnyPermission = (permissions) => {
  if (!Array.isArray(permissions)) {
    return false;
  }
  
  return permissions.some(permission => hasPermission(permission));
};

/**
 * Check if user has ALL of the specified permissions (AND logic)
 * @param {Array<string>} permissions - Array of permissions to check
 * @returns {boolean} True if user has all permissions
 */
export const hasAllPermissions = (permissions) => {
  if (!Array.isArray(permissions)) {
    return false;
  }
  
  return permissions.every(permission => hasPermission(permission));
};

/**
 * Check if user has a specific role
 * @param {string} role - Role to check (e.g., 'ADMIN', 'HR_MANAGER')
 * @returns {boolean} True if user has the role
 */
export const hasRole = (role) => {
  const roles = getUserRoles();
  return roles.includes(role);
};

/**
 * Check if user has ANY of the specified roles (OR logic)
 * @param {Array<string>} roles - Array of roles to check
 * @returns {boolean} True if user has at least one role
 */
export const hasAnyRole = (roles) => {
  if (!Array.isArray(roles)) {
    return false;
  }
  
  const userRoles = getUserRoles();
  return roles.some(role => userRoles.includes(role));
};

/**
 * Check if user is admin
 * @returns {boolean} True if user has ADMIN role
 */
export const isAdmin = () => {
  return hasRole('ADMIN');
};

/**
 * Calculate effective permissions from roles and overrides
 * This should match the backend logic
 * @param {Array<string>} roles - User roles
 * @param {Object} permissionOverrides - Permission overrides
 * @returns {Array<string>} Effective permissions
 */
export const calculateEffectivePermissions = (roles, permissionOverrides = {}) => {
  const ROLE_TEMPLATES = {
    ADMIN: ['*'], // All permissions
    HR_MANAGER: [
      'HRM.EMPLOYEE.VIEW', 'HRM.EMPLOYEE.CREATE', 'HRM.EMPLOYEE.EDIT', 'HRM.EMPLOYEE.DELETE',
      'HRM.EMPLOYEE.SALARY.VIEW', 'HRM.EMPLOYEE.SALARY.EDIT',
      'HRM.ATTENDANCE.VIEW', 'HRM.ATTENDANCE.MANAGE',
      'HRM.LEAVE.VIEW', 'HRM.LEAVE.APPROVE', 'HRM.LEAVE.MANAGE',
      'HRM.PERFORMANCE.VIEW', 'HRM.PERFORMANCE.MANAGE',
      'HRM.ASSETS.VIEW', 'HRM.ASSETS.MANAGE',
      'HRM.DOCUMENTS.VIEW', 'HRM.DOCUMENTS.MANAGE',
      'NOTIFICATIONS.PREFERENCES.MANAGE', 'AUDIT.VIEW'
    ],
    HR_STAFF: [
      'HRM.EMPLOYEE.VIEW', 'HRM.EMPLOYEE.CREATE', 'HRM.EMPLOYEE.EDIT',
      'HRM.ATTENDANCE.VIEW', 'HRM.ATTENDANCE.MANAGE',
      'HRM.LEAVE.VIEW', 'HRM.PERFORMANCE.VIEW',
      'HRM.ASSETS.VIEW', 'HRM.DOCUMENTS.VIEW', 'HRM.DOCUMENTS.MANAGE'
    ],
    EMPLOYEE: [
      'HRM.EMPLOYEE.VIEW', 'HRM.ATTENDANCE.VIEW', 'HRM.LEAVE.VIEW',
      'HRM.PERFORMANCE.VIEW', 'HRM.DOCUMENTS.VIEW',
      'NOTIFICATIONS.PREFERENCES.MANAGE'
    ],
    POS_MANAGER: [
      'POS.BILLING.VIEW', 'POS.BILLING.CREATE', 'POS.BILLING.EDIT',
      'POS.BILLING.VOID', 'POS.BILLING.REFUND', 'AUDIT.VIEW'
    ],
    CASHIER: [
      'POS.BILLING.VIEW', 'POS.BILLING.CREATE'
    ]
  };
  
  const permissions = new Set();
  
  // Add permissions from all roles
  roles.forEach(role => {
    const rolePerms = ROLE_TEMPLATES[role] || [];
    rolePerms.forEach(perm => permissions.add(perm));
  });
  
  // Apply overrides
  Object.entries(permissionOverrides).forEach(([perm, allowed]) => {
    if (allowed) {
      permissions.add(perm);
    } else {
      permissions.delete(perm);
    }
  });
  
  return Array.from(permissions);
};

/**
 * Store user permissions in localStorage after login
 * @param {Object} userData - User data from login response
 */
export const storeUserPermissions = (userData) => {
  try {
    // Calculate effective permissions if not provided
    if (!userData.permissions && userData.roles) {
      userData.permissions = calculateEffectivePermissions(
        userData.roles,
        userData.permissionOverrides || {}
      );
    }
    
    localStorage.setItem('userData', JSON.stringify(userData));
  } catch (error) {
    console.error('Error storing user permissions:', error);
  }
};

/**
 * Clear user permissions from localStorage (on logout)
 */
export const clearUserPermissions = () => {
  try {
    localStorage.removeItem('userData');
  } catch (error) {
    console.error('Error clearing user permissions:', error);
  }
};

// Permission constants for easy reference
export const PERMISSIONS = {
  // HRM
  HRM_EMPLOYEE_VIEW: 'HRM.EMPLOYEE.VIEW',
  HRM_EMPLOYEE_CREATE: 'HRM.EMPLOYEE.CREATE',
  HRM_EMPLOYEE_EDIT: 'HRM.EMPLOYEE.EDIT',
  HRM_EMPLOYEE_DELETE: 'HRM.EMPLOYEE.DELETE',
  HRM_EMPLOYEE_SALARY_VIEW: 'HRM.EMPLOYEE.SALARY.VIEW',
  HRM_EMPLOYEE_SALARY_EDIT: 'HRM.EMPLOYEE.SALARY.EDIT',
  HRM_ATTENDANCE_VIEW: 'HRM.ATTENDANCE.VIEW',
  HRM_ATTENDANCE_MANAGE: 'HRM.ATTENDANCE.MANAGE',
  HRM_LEAVE_VIEW: 'HRM.LEAVE.VIEW',
  HRM_LEAVE_APPROVE: 'HRM.LEAVE.APPROVE',
  HRM_PERFORMANCE_VIEW: 'HRM.PERFORMANCE.VIEW',
  HRM_PERFORMANCE_MANAGE: 'HRM.PERFORMANCE.MANAGE',
  HRM_ASSETS_VIEW: 'HRM.ASSETS.VIEW',
  HRM_ASSETS_MANAGE: 'HRM.ASSETS.MANAGE',
  HRM_DOCUMENTS_VIEW: 'HRM.DOCUMENTS.VIEW',
  HRM_DOCUMENTS_MANAGE: 'HRM.DOCUMENTS.MANAGE',
  
  // Notifications
  NOTIFICATIONS_CONFIG_MANAGE: 'NOTIFICATIONS.CONFIG.MANAGE',
  NOTIFICATIONS_PREFERENCES_MANAGE: 'NOTIFICATIONS.PREFERENCES.MANAGE',
  
  // Audit
  AUDIT_VIEW: 'AUDIT.VIEW',
  AUDIT_EXPORT: 'AUDIT.EXPORT',
  
  // POS
  POS_BILLING_VIEW: 'POS.BILLING.VIEW',
  POS_BILLING_CREATE: 'POS.BILLING.CREATE',
  POS_BILLING_EDIT: 'POS.BILLING.EDIT',
  POS_BILLING_VOID: 'POS.BILLING.VOID',
  POS_BILLING_REFUND: 'POS.BILLING.REFUND',
  
  // POS AI Features
  POS_AI_VOICE_ORDERING: 'POS.AI.VOICE_ORDERING',
  POS_AI_SMART_SUGGESTIONS: 'POS.AI.SMART_SUGGESTIONS',
  POS_AI_CUSTOMER_INTELLIGENCE: 'POS.AI.CUSTOMER_INTELLIGENCE',
  
  // System
  SYSTEM_SETTINGS_MANAGE: 'SYSTEM.SETTINGS.MANAGE',
  SYSTEM_USERS_MANAGE: 'SYSTEM.USERS.MANAGE',
  SYSTEM_BRANCHES_MANAGE: 'SYSTEM.BRANCHES.MANAGE',
};

export default {
  getUserPermissions,
  getUserRoles,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  hasRole,
  hasAnyRole,
  isAdmin,
  calculateEffectivePermissions,
  storeUserPermissions,
  clearUserPermissions,
  PERMISSIONS,
};
